import {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { getMapKeyTypeDisplayName } from '~/sidebar/sidebarApps/rightSidebar/masterKey/masterKey.helpers';
import { MapKeyType } from '~/sidebar/sidebarApps/rightSidebar/masterKey/masterKey.types';
import { publicMapSettingsSetMapKeyType } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.actionCreators';
import { usePublicMapSettingsMapKeyTypeDefaultOption } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { SettingsAccordionDropdownComponent } from '../../settingsAccordionDropdown.component';
import { SettingsAccordionDropDownOptionComponent } from '../../settingsAccordionDropdownOption.component';

export const useSharedPresentationalMapsMapKeyDropdown = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const currentValue = usePublicMapSettingsMapKeyTypeDefaultOption();

  const onDropdownChange = useCallback((type: MapKeyType) => () => (
    dispatch(publicMapSettingsSetMapKeyType(type))
  ), [dispatch]);

  const onAccordionHeaderClick = useCallback(() => {
    setIsDropdownOpen(isOpen => !isOpen);
  }, []);

  return useMemo(() => {
    const options = Object.values(MapKeyType).map(value => ({
      value, name: getMapKeyTypeDisplayName(value, t),
    }));

    const renderedOptions = options
      .map((option, index) => (
        <SettingsAccordionDropDownOptionComponent
          key={option.value}
          isLast={index === options.length - 1}
          title={option.name}
          onChange={onDropdownChange(option.value)}
        />
      ));

    const triggerComponent = (
      <SettingsAccordionDropdownComponent
        isOpen={isDropdownOpen}
        onClick={onAccordionHeaderClick}
        triggerText={getMapKeyTypeDisplayName(currentValue, t)}
      />
    );

    return {
      isOpen: isDropdownOpen,
      onClose: () => setIsDropdownOpen(false),
      renderedOptions,
      triggerComponent,
      value: currentValue,
    };
  }, [currentValue, isDropdownOpen, onAccordionHeaderClick, onDropdownChange, t]);
};
